import { TokenUid, Usd } from "@silo/types";
import { TokenMap } from "@silo/utils";
import { Array, Effect, pipe } from "effect";
import { KvClientError, KvService, KvTokenPrice } from "./types";
import { getTokenPriceKey } from "./keys";
import { Schema } from "@effect/schema";
import { isNotNullable } from "effect/Predicate";

export type GetTokenPriceMap = ReturnType<typeof getTokenPriceMap>;
export type GetTokenPriceMapSuccess = Effect.Effect.Success<GetTokenPriceMap>;
export type GetTokenPriceMapError = Effect.Effect.Error<GetTokenPriceMap>;
export type GetTokenPriceMapContext = Effect.Effect.Context<GetTokenPriceMap>;

export function getTokenPriceMap(tokens: TokenUid[]) {
  return Effect.gen(function* () {
    if (tokens.length === 0) {
      return new TokenMap<Usd>();
    }

    const kv = yield* KvService;

    const results = yield* Effect.tryPromise({
      try: async () => {
        const pipeline = kv.pipeline();

        for (const { chainKey, tokenAddress } of tokens) {
          const key = getTokenPriceKey(chainKey, tokenAddress);

          pipeline.get(key);
        }

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    const prices = yield* Effect.all(
      tokens.map((token, i) =>
        pipe(
          results,
          Array.get(i),
          Effect.andThen(Schema.decodeUnknown(Schema.NullOr(KvTokenPrice))),
          Effect.andThen((priceUsd) => ({ token, priceUsd }))
        )
      )
    );

    const tokenPriceMap = new TokenMap<Usd>();

    for (const { token, priceUsd } of prices) {
      if (isNotNullable(priceUsd)) {
        tokenPriceMap.set(token, priceUsd);
      }
    }

    return tokenPriceMap;
  });
}
